import React, { ReactElement } from "react";
import { createListTableColumns, ensure, LabeledText, Link, ListTableColumn, renderJoined } from "sprint-common-web";

import { ContentReferenceType } from "../../../common/types/ContentReferenceType";
import { OrderContentReference, OrderContentTvSeriesReference } from "../../../common/types/OrderContentReference";
import { renderContent } from "../../file-records/common/components/renderContent";
import { LookupContentItem } from "./LookupContentItem";

interface CreateLookupContentListTableColumnsProps {
    onClick: (item: LookupContentItem) => void;
}

export const createLookupContentListTableColumns = (
    props: CreateLookupContentListTableColumnsProps
): ListTableColumn<LookupContentItem>[] => {
    return createListTableColumns<LookupContentItem>([
        {
            type: "interactive",
            key: "type",
            name: "Title",
            minWidth: 65,
            maxWidth: 350,
            isSortable: true,
            onRender: renderLookupContent,
            onItemInvoked: (item) => props.onClick(item)
        },
        {
            type: "basic",
            key: "distributor",
            name: "Distributor",
            minWidth: 90,
            maxWidth: 120,
            isSortable: false
        },
        {
            type: "basic",
            key: "versionType",
            name: "Version Type",
            minWidth: 90,
            maxWidth: 120,
            isSortable: false
        },
        {
            type: "basic",
            key: "brandings",
            name: "Brandings",
            minWidth: 90,
            maxWidth: 120,
            isSortable: false,
            onRender: renderJoined
        },
        {
            type: "basic",
            key: "version",
            name: "Buzz Ids",
            minWidth: 120,
            maxWidth: 160,
            isSortable: false,
            onRender: renderBuzzIds
        }
    ]);
};

const renderLookupContent = (
    item?: LookupContentItem,
    _index?: number,
    column?: ListTableColumn<unknown>
): ReactElement | null => {
    if (!item || !column) return null;

    const onClick = () => column.onItemInvoked && column?.onItemInvoked(item);

    return (
        <Link onClick={onClick}>
            {renderContent({
                content: resolveContentTitle(item),
                contentType: item.type
            })}
        </Link>
    );
};

const renderBuzzIds = (item?: LookupContentItem): ReactElement | null => {
    ensure(item);

    return (
        <>
            <LabeledText
                label="Version id:"
                labelFontSize={12}
                textFontSize={12}
                value={item.version.buzzId}
                direction="horizontal"
            />
            <LabeledText
                label="Content id:"
                labelFontSize={12}
                textFontSize={12}
                value={resolveContentBuzzId(item)}
                direction="horizontal"
            />
        </>
    );
};

const resolveContentTitle = (content: OrderContentReference): string => {
    switch (content.type) {
        case ContentReferenceType.Movie: {
            return content.movie.title;
        }
        case ContentReferenceType.TvSeries: {
            return resolveTvSeriesTitle(content);
        }
        case ContentReferenceType.Ancillary: {
            return content.ancillary.title;
        }
        default:
            throw new Error("Incorrect Content Type.");
    }
};

const resolveContentBuzzId = (content: OrderContentReference): string | undefined => {
    switch (content.type) {
        case ContentReferenceType.Movie: {
            return content.movie.buzzId;
        }
        case ContentReferenceType.TvSeries: {
            return content.episode.buzzId;
        }
        case ContentReferenceType.Ancillary: {
            return content.ancillary.buzzId;
        }
        default:
            throw new Error("Incorrect Content Type.");
    }
};

const resolveTvSeriesTitle = (tvContent: OrderContentTvSeriesReference) => {
    return [tvContent.tvSeries.title, tvContent.season?.name, tvContent.episode?.title].filter(Boolean).join(" - ");
};
